/**
 * YesterdayWins.tsx — W50 AE Today V2 Block E
 *
 * Shows CP answers + contacts added yesterday.
 * Empty state: "No activity recorded yesterday."
 *
 * Canon: No exclamation points. No emojis. M-PACT hyphenated.
 * 5-color palette: #6B7280, #DC2626, #EA580C, #EAB308, #16A34A.
 */

import React from "react";
import { useLocation } from "wouter";

const BG = "#F9FAFB";          // page background -- canon light
const CARD = "#FFFFFF";        // card background -- canon light
const BORDER = "#E5E7EB";      // border -- canon light
const MI6_RED = "#DC2626";
const TEXT_PRIMARY = "#111827"; // dark text on light
const TEXT_SECONDARY = "#374151";
const TEXT_MUTED = "#6B7280";
const GREEN = "#16A34A";
const BLUE = "#0B0B0F";

interface WinEvent {
  event_type: "cp_answered" | "contact_added";
  account_name: string;
  account_id: number;
  cp_number: number | null;
  cp_label: string | null;
  contact_name?: string;
  contact_role?: string | null;
  occurred_at: string;
  label: string;
}

interface YesterdayWinsProps {
  wins: WinEvent[];
  loading?: boolean;
}

function fmtTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function yesterdayLabel(): string {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

function eventIcon(type: string): { label: string; bg: string; color: string } {
  if (type === "cp_answered") return { label: "CP answered", bg: "#1C2A1C", color: GREEN };
  if (type === "contact_added") return { label: "Contact added", bg: "#1C1C2A", color: BLUE };
  return { label: type, bg: BG, color: TEXT_MUTED };
}

export default function YesterdayWins({ wins, loading = false }: YesterdayWinsProps) {
  const [, setLocation] = useLocation();

  return (
    <section
      className="rounded-lg border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      {/* Section header */}
      <div className="mb-4">
        <span
          className="text-xs uppercase tracking-widest font-semibold"
          style={{ color: MI6_RED }}
        >
          Block E
        </span>
        <h2 className="text-base font-semibold mt-0.5" style={{ color: TEXT_PRIMARY }}>
          Yesterday's Activity
        </h2>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          {yesterdayLabel()}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-8 rounded animate-pulse"
              style={{ background: BORDER, opacity: 0.6 - i * 0.15 }}
            />
          ))}
        </div>
      ) : wins.length === 0 ? (
        <p className="text-sm py-2" style={{ color: TEXT_MUTED }}>
          No activity recorded yesterday.
        </p>
      ) : (
        <ul className="space-y-3">
          {wins.map((win, idx) => {
            const icon = eventIcon(win.event_type);
            return (
              <li
                key={`${win.event_type}-${win.account_id}-${idx}`}
                className="flex items-start gap-3 border-t pt-3 first:border-0 first:pt-0"
                style={{ borderColor: BORDER }}
              >
                {/* Event type badge */}
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 mt-0.5"
                  style={{ background: icon.bg, color: icon.color }}
                >
                  {icon.label}
                </span>

                {/* Event label */}
                <div className="flex-1 min-w-0">
                  <button
                    className="text-sm text-left hover:underline"
                    style={{ color: TEXT_PRIMARY }}
                    onClick={() => setLocation(`/account/${win.account_id}`)}
                  >
                    {win.label}
                  </button>
                </div>

                {/* Time */}
                <span className="text-xs flex-shrink-0" style={{ color: TEXT_MUTED }}>
                  {fmtTime(win.occurred_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
